import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, RefreshCw, ShieldOff } from 'lucide-react';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { api, type WithheldLog, type WithheldLogEntry } from '../../lib/api';
import { cn, formatDate, timeAgo } from '../../lib/utils';

/**
 * The guardrail log: every passage the ingestion agent kept out of the knowledge base, and why.
 *
 * The categories are the sources hub's screening categories; the difference is that the hub holds a
 * whole event for a person, while the ingestor withholds only the passages, on its own, when it is
 * confident — so this log is the only place a person can check what it decided. Admin-only because it
 * holds the withheld text itself, which is by construction what should not be searchable by
 * colleagues. Each passage is hidden until shown, for the same reason.
 */
export default function AdminGuardrail() {
  const [log, setLog] = useState<WithheldLog | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.guardrail
      .withheld(category ? { category } : {})
      .then((page) => {
        setLog(page);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const loadMore = () => {
    if (!log?.nextBefore) return;
    setLoadingMore(true);
    api.guardrail
      .withheld({ ...(category ? { category } : {}), before: log.nextBefore })
      .then((page) => setLog({ ...page, entries: [...log.entries, ...page.entries] }))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoadingMore(false));
  };

  const total = log?.byCategory.reduce((sum, c) => sum + c.count, 0) ?? 0;
  const present = log?.byCategory.filter((c) => c.count > 0) ?? [];

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <AdminSidebar />

      <div className="pl-56 flex-1 h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-7 py-7">
          <div className="flex items-start gap-3 mb-5">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-ink">Guardrail log</h1>
              <p className="text-xs text-ink-muted mt-1 max-w-2xl leading-relaxed">
                Passages the ingestion agent kept out of the knowledge base — health, family, personal
                finances, credentials and the rest of the sources hub's screening categories. Withheld
                during placement, a passage was cut out before any project saw it; during writing, the
                writer declared it and every page or citation that repeated it was refused. Only passages
                the agent was confident about are withheld; the rest stay with the write phase, which
                flags anything sensitive it keeps for review. Spam and content with no business purpose
                as a whole are not withheld but discarded during placement, with their category on the run.
              </p>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="ml-auto text-ink-faint hover:text-ink transition-colors disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : undefined} />
            </button>
          </div>

          {/* Counted over everything, category aside, so choosing one does not move the others. */}
          {total > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-4 pb-3 border-b border-border-subtle">
              <Chip active={category === null} onClick={() => setCategory(null)}>
                All · {total}
              </Chip>
              {present.map((c) => (
                <Chip key={c.category} active={category === c.category} onClick={() => setCategory(c.category)}>
                  {c.label} · {c.count}
                </Chip>
              ))}
            </div>
          )}

          {error && (
            <p className="mb-4 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">
              {error}
            </p>
          )}

          {loading && !log ? (
            <p className="flex items-center gap-2 text-sm text-ink-faint py-8 justify-center">
              <Loader2 size={14} className="animate-spin" />
              Loading…
            </p>
          ) : (log?.entries.length ?? 0) === 0 ? (
            <div className="border border-dashed border-border rounded-lg py-14 text-center">
              <ShieldOff size={22} className="text-border mx-auto mb-3" />
              <p className="text-sm text-ink-secondary mb-1">Nothing withheld</p>
              <p className="text-xs text-ink-faint max-w-sm mx-auto">
                {category
                  ? 'Nothing in this category has been withheld.'
                  : 'The ingestion agent has not withheld any passage yet.'}
              </p>
            </div>
          ) : (
            <>
              <ul className="space-y-2.5">
                {log?.entries.map((entry) => <Entry key={entry.id} entry={entry} />)}
              </ul>
              {log?.nextBefore && (
                <div className="mt-4 text-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="text-xs text-ink-muted hover:text-ink transition-colors disabled:opacity-40"
                  >
                    {loadingMore ? 'Loading…' : 'Older'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
        active ? 'bg-ink text-surface border-ink' : 'bg-card text-ink-muted border-border hover:border-orange-300 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

function Entry({ entry }: { entry: WithheldLogEntry }) {
  const [shown, setShown] = useState(false);
  return (
    <li className="bg-card border border-border rounded-lg px-4 py-3">
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-xs font-semibold text-ink">{entry.categoryLabel}</span>
        <span className="text-[11px] text-ink-faint">
          {Math.round(entry.confidence * 100)}% · {entry.phase === 'PLACEMENT' ? 'cut before writing' : 'refused while writing'}
        </span>
        <span className="ml-auto text-[11px] text-ink-faint" title={formatDate(entry.createdAt)}>
          {timeAgo(entry.createdAt)}
        </span>
      </div>
      <p className="text-xs text-ink-secondary mt-1">{entry.reason}</p>

      <div className="mt-2 flex items-start gap-2">
        <button
          onClick={() => setShown((v) => !v)}
          className="mt-0.5 text-ink-faint hover:text-ink transition-colors flex-shrink-0"
          title={shown ? 'Hide the passage' : 'Show the passage'}
        >
          {shown ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
        {shown ? (
          <pre className="flex-1 min-w-0 whitespace-pre-wrap break-words text-[11px] font-mono text-ink bg-surface-subtle rounded px-2 py-1.5 max-h-48 overflow-y-auto">
            {entry.text}
          </pre>
        ) : (
          <span className="text-[11px] text-ink-faint italic">{entry.text.length} characters, hidden</span>
        )}
      </div>

      <div className="mt-2 text-[11px] text-ink-faint flex flex-wrap gap-x-2">
        <span>{entry.project.name}</span>
        {entry.feedItem && <span>· {entry.feedItem.source}</span>}
        <Link to={`/activity?run=${entry.runId}`} className="text-orange-600 hover:underline">
          · the run
        </Link>
      </div>
    </li>
  );
}
